import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cricketPayload, cricketSideError } from '@/lib/cricket';

const num = 'h-10 text-center text-lg font-bold';

const ScoreRow = ({ name, value, onChange, testId, onEnter, autoFocus, cricket, wickets, onWickets, overs, onOvers }) => (
  <div className="flex items-center gap-2">
    <span className="flex-1 min-w-0 truncate font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>{name}</span>
    <Input type="number" min="0" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus}
      placeholder={cricket ? 'Runs' : undefined} onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }}
      className={`${cricket ? 'w-16' : 'w-20'} ${num}`} data-testid={testId} />
    {cricket && (
      <>
        <span className="text-slate-400 font-bold">/</span>
        <Input type="number" min="0" max="10" inputMode="numeric" placeholder="Wkts" value={wickets} onChange={(e) => onWickets(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }} className={`w-16 ${num}`} data-testid={`${testId}-wickets`} />
        <Input inputMode="decimal" placeholder="Overs" value={overs} onChange={(e) => onOvers(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }} className="w-16 h-10 text-center" data-testid={`${testId}-overs`} />
      </>
    )}
  </div>
);

const str = (v) => (v === null || v === undefined ? '' : String(v));

export const ScoreEditDialog = ({ match, onClose, onSave, cricket }) => {
  const [v, setV] = useState({ a: '', b: '', wa: '', wb: '', oa: '', ob: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (match) setV({ a: str(match.score_a), b: str(match.score_b), wa: str(match.wickets_a), wb: str(match.wickets_b), oa: str(match.overs_a), ob: str(match.overs_b) });
  }, [match]);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const valid = v.a !== '' && v.b !== '' && Number(v.a) >= 0 && Number(v.b) >= 0;
  const tied = valid && Number(v.a) === Number(v.b);
  const save = async () => {
    if (!valid || saving) return;
    let extra;
    if (cricket) {
      const err = cricketSideError(match.team_a_name || 'Team A', v.wa, v.oa) || cricketSideError(match.team_b_name || 'Team B', v.wb, v.ob);
      if (err) { toast.error(err); return; }
      extra = cricketPayload(v.wa, v.wb, v.oa, v.ob);
    }
    setSaving(true);
    const ok = await onSave(match.id, parseInt(v.a, 10), parseInt(v.b, 10), extra);
    setSaving(false);
    if (ok) onClose();
  };
  const id = match?.id;
  return (
    <Dialog open={!!match} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={cricket ? 'sm:max-w-md' : 'sm:max-w-sm'} data-testid="bracket-score-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D] uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{match?.match_label || 'Enter score'}</DialogTitle>
          <DialogDescription>
            {cricket ? 'Runs / wickets for each team; overs are optional (e.g. 18.4). The winner advances automatically.' : 'The winner advances automatically.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <ScoreRow name={match?.team_a_name} value={v.a} onChange={set('a')} onEnter={save} testId={`bracket-score-a-${id}`} autoFocus
            cricket={cricket} wickets={v.wa} onWickets={set('wa')} overs={v.oa} onOvers={set('oa')} />
          <ScoreRow name={match?.team_b_name} value={v.b} onChange={set('b')} onEnter={save} testId={`bracket-score-b-${id}`}
            cricket={cricket} wickets={v.wb} onWickets={set('wb')} overs={v.ob} onOvers={set('ob')} />
        </div>
        {tied && (
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="bracket-score-tie-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" />
            {cricket ? 'Tied score: no team will advance. Add the Super Over result to decide a winner.' : 'Tied score: no team will advance. Include overtime or shootout points to decide a winner.'}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bracket-score-cancel">Cancel</Button>
          <Button onClick={save} disabled={!valid || saving} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid={`bracket-save-score-${id}`}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save score
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
